import type { Lead } from "@/types/lead";

export const DAY_OPTIONS = [7, 14, 30, 90] as const;
export type DayRange = (typeof DAY_OPTIONS)[number];

export interface LeadRow {
  id: string;
  status: string;
  created_at: string;
  data: Lead | null;
}

export interface BreakdownEntry {
  label: string;
  count: number;
}

export interface DashboardMetrics {
  totalLeads: number;
  sentLeads: number;
  draftLeads: number;
  todayLeads: number;
  todaySent: number;
  activeMembers: number;
  dailyBreakdown: { date: string; total: number; sent: number }[];
  sourceBreakdown: BreakdownEntry[];
  productBreakdown: BreakdownEntry[];
}

export function toYMD(iso: string): string {
  return iso.slice(0, 10);
}

export function buildDailyBreakdown(rows: LeadRow[], days: number): { date: string; total: number; sent: number }[] {
  const map = new Map<string, { total: number; sent: number }>();
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    map.set(toYMD(d.toISOString()), { total: 0, sent: 0 });
  }
  for (const r of rows) {
    const day = toYMD(r.created_at);
    if (!map.has(day)) continue;
    const entry = map.get(day)!;
    entry.total++;
    if (r.status === "sent" || r.status === "archived") entry.sent++;
  }
  return Array.from(map.entries()).map(([date, v]) => ({ date, ...v }));
}

export function buildBreakdown(rows: LeadRow[], key: (r: LeadRow) => string | null | undefined): BreakdownEntry[] {
  const counts = new Map<string, number>();
  for (const r of rows) {
    const val = (key(r) ?? "unknown").toString().trim() || "unknown";
    counts.set(val, (counts.get(val) ?? 0) + 1);
  }
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count);
}

export function fmtSource(s: string): string {
  const map: Record<string, string> = {
    facebook: "Facebook",
    google: "Google",
    shedsforsale_com: "ShedsForSale",
    drive_by: "Drive-by",
    word_of_mouth: "Word of mouth",
    referral: "Referral",
    bdc_call: "BDC Call",
    other: "Other",
    unknown: "Unknown",
  };
  return map[s] ?? s;
}
